/**
 * Set of "stateful" tools that should be coalesced (only show latest call).
 * These are tools where each call represents the current state, not a separate action.
 * For example, TodoWrite with 5 completed tasks supersedes TodoWrite with 3 completed tasks.
 */
const STATEFUL_TOOLS = new Set([
  "todowrite",
  "todoread",
  // Add more stateful tools here as needed (e.g., "createplan")
]);

/**
 * Get human-readable display name for a tool.
 * Matches opencode-web's TUI spec for consistency.
 */
const getToolDisplayName = (toolName: string): string => {
  const normalized = toolName.toLowerCase();

  // Handle MCP tools (mcp_*, localmcp_*, or Replo_* / playwright_*)
  if (normalized.startsWith("mcp_") || normalized.startsWith("localmcp_")) {
    const cleanName = normalized.replace(/^(local)?mcp_/, "");
    return cleanName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Handle Replo MCP tools (e.g., Replo_search_assets, Replo_search_products)
  if (normalized.startsWith("replo_")) {
    const cleanName = normalized.replace(/^replo_/, "");
    return cleanName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Handle Playwright browser tools (e.g., playwright_browser_navigate, playwright_browser_click)
  if (normalized.startsWith("playwright_browser_")) {
    const cleanName = normalized.replace(/^playwright_browser_/, "");
    const formattedName = cleanName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `Browser: ${formattedName}`;
  }

  // Specific tool name mappings
  const toolNames: Record<string, string> = {
    webfetch: "Fetch",
    todowrite: "Plan",
    todoread: "Plan",
    read: "Read",
    edit: "Edit",
    write: "Write",
    bash: "Bash",
    glob: "Find Files",
    grep: "Search",
    list: "List",
    task: "Task",
    patch: "Patch",
  };

  if (toolNames[normalized]) {
    return toolNames[normalized];
  }

  // Default: title case the tool name
  return toolName.charAt(0).toUpperCase() + toolName.slice(1);
};

/**
 * Check if a tool is "stateful" (should be coalesced to show only latest).
 */
export const isStatefulTool = (toolName: string): boolean => {
  return STATEFUL_TOOLS.has(toolName.toLowerCase());
};

/**
 * Check if a tool is the AskUserQuestion tool which renders its own interactive UI.
 */
export const isQuestionTool = (toolName: string): boolean => {
  return toolName.toLowerCase() === "question";
};

/**
 * Check if a tool is the show_integration_status MCP tool.
 * MCP tool names are prefixed by OpenCode with the server config key
 * (e.g. "replo_show_integration_status"), so we use endsWith to match.
 */
export const isIntegrationStatusTool = (toolName: string): boolean => {
  return toolName.toLowerCase().endsWith("show_integration_status");
};

/**
 * Check if a tool is the Task tool (used to spawn subagents).
 */
export const isTaskTool = (toolName: string): boolean => {
  return toolName.toLowerCase() === "task";
};

/**
 * Check if a tool is the skill loader tool.
 */
export const isSkillTool = (toolName: string): boolean => {
  return toolName.toLowerCase() === "skill";
};

/**
 * Get a relative path by removing common prefixes.
 * Makes file paths more readable in tool titles.
 */
const getRelativePath = (filePath: string): string => {
  // Remove common absolute path prefixes
  return filePath
    .replace(/^\/?(workspace|home|Users\/[^/]+)\/[^/]+\//, "")
    .replace(/^\/?(app|src)\//, "$1/");
};

/**
 * Get contextual tool title with file paths or command info.
 * Inspired by opencode-web's TUI_STATUS_DISPLAY_SPEC.md
 * Examples: "Edit src/app/page.tsx", "Bash npm install", "Fetch github.com"
 */
export const getContextualToolTitle = (
  toolName: string,
  input?: Record<string, unknown>,
): string => {
  const normalized = toolName.toLowerCase();

  // Extract file path from common input fields
  const filePath = (input?.path ?? input?.filePath ?? input?.file_path) as
    | string
    | undefined;
  const relativePath = filePath ? getRelativePath(filePath) : undefined;

  switch (normalized) {
    case "read":
    case "edit":
    case "write":
      return relativePath
        ? `${getToolDisplayName(toolName)} ${relativePath}`
        : getToolDisplayName(toolName);
    case "bash": {
      const desc = (input?.description ?? input?.command) as string | undefined;
      return desc ? `Bash ${desc}` : getToolDisplayName(toolName);
    }
    case "webfetch": {
      const url = input?.url as string | undefined;
      if (url) {
        try {
          return `Fetch ${new URL(url).hostname}`;
        } catch {
          return `Fetch ${url}`;
        }
      }
      return getToolDisplayName(toolName);
    }
    case "glob": {
      const pattern = input?.pattern as string | undefined;
      return pattern ? `Find ${pattern}` : getToolDisplayName(toolName);
    }
    case "grep": {
      const searchPattern = input?.pattern as string | undefined;
      return searchPattern
        ? `Search ${searchPattern}`
        : getToolDisplayName(toolName);
    }
    case "replo_search_assets": {
      const searchTerm = input?.searchTerm as string | undefined;
      return searchTerm
        ? `Search assets for "${searchTerm}"`
        : getToolDisplayName(toolName);
    }
    case "replo_search_products": {
      const searchTerm = input?.searchTerm as string | undefined;
      return searchTerm
        ? `Search products for "${searchTerm}"`
        : getToolDisplayName(toolName);
    }
    case "playwright_browser_navigate": {
      const url = input?.url as string | undefined;
      if (url) {
        try {
          return `Browser: Navigate to ${new URL(url).hostname}`;
        } catch {
          return `Browser: Navigate to ${url}`;
        }
      }
      return getToolDisplayName(toolName);
    }
    case "playwright_browser_click": {
      const selector = input?.selector as string | undefined;
      return selector
        ? `Click element: ${selector}`
        : getToolDisplayName(toolName);
    }
    case "playwright_browser_type": {
      const text = input?.text as string | undefined;
      return text
        ? `Browser: Type "${text.slice(0, 30)}${text.length > 30 ? "..." : ""}"`
        : getToolDisplayName(toolName);
    }
    case "skill": {
      const skillName = input?.name as string | undefined;
      return skillName ?? "Skill";
    }
    default:
      return getToolDisplayName(toolName);
  }
};
